import {
  doc, collection, getDoc, getDocs, updateDoc, onSnapshot,
  query, where, orderBy, limit, documentId, increment, arrayUnion, arrayRemove,
} from 'firebase/firestore';
import { db } from './firebase';
// The feed tags each row with the GALLERY kind, because that is what every
// consumer compares against. SOCIAL_KINDS is a different vocabulary
// (project/photo) and has no DESIGN member.
import { ITEM_KINDS } from '../constants/gallery';

// ── SOCIAL: likes, views, comments ───────────────────────────────────────────
// Everything that accumulates ON a piece of work rather than describing it.
//
// TWO KINDS OF TARGET, ONE IMPLEMENTATION. A 3D project and a published photo
// behave identically from a visitor's side — like it, view it, comment on it —
// so they share this file and differ only in which collection they live in and
// what their owner field is called. Two copies of this logic is how the two
// would drift apart; the app already learned that lesson with the gallery
// (see constants/gallery.js).
//
//   project — projects/{id},  owned via `userId`,   public when `shared`
//   photo   — photos/{id},    owned via `ownerUid`, public when `visible`
//
// SHAPE. Counters and the liker list live on the target document itself:
//
//   likesCount  number      — denormalised, so a feed never counts an array
//   likedBy     string[]    — uids, so "have I liked this?" needs no extra read
//   viewsCount  number
//
// A separate likes collection would mean one read per item just to find out
// whether the viewer has liked it. On the document, the answer arrives with the
// item itself. The cost is a bounded array, which is fine at this scale.

//
// READ STRATEGY, and this is the point of the whole file:
//   • feeds and grids → fetchStats(), ONE batched get for many items
//   • an OPEN item     → subscribeSocial(), live
// Live listeners are expensive and only worth it where a number changing under
// your eyes is the feature. On a feed it is just a bill.

const STATS_CHUNK = 30;      // Firestore's ceiling for an `in` query

// The only thing that differs between the two kinds.
export const SOCIAL_KINDS = { PROJECT: 'project', PHOTO: 'photo' };

const TARGETS = {
  [SOCIAL_KINDS.PROJECT]: { col: 'projects', owner: 'userId' },
  [SOCIAL_KINDS.PHOTO]:   { col: 'photos',   owner: 'ownerUid' },
};

// Throws rather than defaulting: a typo'd kind silently writing to the wrong
// collection would be far harder to spot than a crash on the first call.
function target(kind) {
  const t = TARGETS[kind];
  if (!t) throw new Error(`unknown social kind: ${kind}`);
  return t;
}

const docRef = (kind, id) => doc(db, target(kind).col, id);

/* ══════════════════════════════════════════════════════════════════════════
   VIEWS
   ══════════════════════════════════════════════════════════════════════════ */

// One view per item per browser session. Without this a refresh — or React
// re-mounting the page — inflates the count, and the number stops meaning
// anything. sessionStorage rather than localStorage so coming back tomorrow
// counts again, which is what "views" normally means.
function alreadyViewed(token) {
  try {
    const key = `tunify:viewed:${token}`;
    if (sessionStorage.getItem(key)) return true;
    sessionStorage.setItem(key, '1');
    return false;
  } catch {
    // Private mode / storage disabled: count it rather than lose it.
    return false;
  }
}

/**
 * Records a view. Fire-and-forget by design — a failed view must never break
 * the thing the visitor came to look at, and there is nothing useful to tell
 * them if it does.
 */
export async function registerView(kind, id) {
  if (!id || alreadyViewed(`${kind}:${id}`)) return;
  try {
    await updateDoc(docRef(kind, id), { viewsCount: increment(1) });
  } catch {
    /* not public, offline, or rules said no — the item still renders */
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   LIKES
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Toggles the current user's like. Returns the new state so the caller can
 * reconcile its optimistic update.
 *
 * `arrayUnion`/`arrayRemove` and `increment` are resolved SERVER-side, so two
 * people liking at the same moment cannot overwrite each other the way a
 * read-modify-write would. Double-liking is impossible for the same reason:
 * arrayUnion on a uid already present is a no-op... but the counter would
 * still move, so the caller's `liked` flag is what guards it. The rules pin
 * the counter delta to the membership change (see firestore.rules).
 */
export async function toggleLike(kind, id, uid, currentlyLiked) {
  if (!id || !uid) throw new Error('not-signed-in');
  await updateDoc(docRef(kind, id), {
    likedBy: currentlyLiked ? arrayRemove(uid) : arrayUnion(uid),
    likesCount: increment(currentlyLiked ? -1 : 1),
  });
  return !currentlyLiked;
}

/* ══════════════════════════════════════════════════════════════════════════
   READING THE NUMBERS
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * LIVE counters for ONE open item — the project page, or a photo open in the
 * lightbox. Never a grid: see the read-strategy note at the top.
 */
export function subscribeSocial(kind, id, cb) {
  if (!id) return () => {};
  return onSnapshot(
    docRef(kind, id),
    (snap) => {
      const d = snap.exists() ? snap.data() : null;
      cb(d ? {
        likesCount: d.likesCount || 0,
        viewsCount: d.viewsCount || 0,
        likedBy: d.likedBy || [],
      } : null);
    },
    (err) => console.error('[project social] subscribe failed:', err),
  );
}

/**
 * STATIC counters for many items — the feed and grid path.
 *
 * One `in` query per 30 ids instead of a listener (or a read) per tile. A grid
 * of twelve items costs a single query and never re-reads.
 */
export async function fetchStats(kind, targetIds) {
  const ids = [...new Set((targetIds || []).filter(Boolean))];
  if (!ids.length) return {};

  const chunks = [];
  for (let i = 0; i < ids.length; i += STATS_CHUNK) chunks.push(ids.slice(i, i + STATS_CHUNK));

  const out = {};
  await Promise.all(chunks.map(async (chunk) => {
    try {
      const snap = await getDocs(query(
        collection(db, target(kind).col),
        where(documentId(), 'in', chunk),
      ));
      snap.docs.forEach(d => {
        const v = d.data();
        out[d.id] = {
          likesCount: v.likesCount || 0,
          viewsCount: v.viewsCount || 0,
          // Maintained by functions/onCommentWrite.js. Free here — it rides
          // along on documents this query was fetching anyway.
          commentsCount: v.commentsCount || 0,
        };
      });
    } catch (err) {
      // An item that isn't public is unreadable — that's the rules working,
      // not a failure. Those tiles simply show no numbers.
      console.warn('[project stats] chunk failed:', err?.code || err);
    }
  }));
  return out;
}

/**
 * Everything this user has liked, across both kinds.
 *
 * `likedBy` is an array on the item, so one `array-contains` query per
 * collection answers it — no join, and no separate "likes" collection to keep
 * in step. This is the payoff for putting the liker list on the document.
 *
 * The visibility filter is NOT decoration. Firestore rejects any query whose
 * results *could* include documents the rules forbid, so `visible == true` /
 * `shared == true` is what makes these queries legal at all — without it the
 * whole read is denied, not merely filtered. Both need a composite index
 * (see firestore.indexes.json).
 *
 * An item you liked and its owner later hid simply stops appearing, which is
 * the correct outcome: it is no longer public.
 */
export async function fetchLikedByUser(uid, max = 60) {
  if (!uid) return { photos: [], projects: [] };

  const run = async (col, publicField) => {
    try {
      const snap = await getDocs(query(
        collection(db, col),
        where('likedBy', 'array-contains', uid),
        where(publicField, '==', true),
        limit(max),
      ));
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (err) {
      console.warn(`[liked] ${col} query failed:`, err?.code || err);
      return [];
    }
  };

  const [photos, projects] = await Promise.all([
    run('photos', 'visible'),
    run('projects', 'shared'),
  ]);
  return { photos, projects };
}

/* ══════════════════════════════════════════════════════════════════════════
   THE FEED'S CANDIDATE POOL
   ══════════════════════════════════════════════════════════════════════════
   Every published photo and design, newest first, from which the dashboard
   draws its weighted-random selection.

   CACHED AT MODULE LEVEL, and this is the single biggest saving in the app.
   These documents carry their THUMBNAILS inline (Storage isn't provisioned —
   see galleryService), so the pool is not 240 cheap rows, it is a few megabytes.
   Refetching it every time someone lands on the dashboard — which is the app's
   home, visited constantly — was paying that bill over and over for a list
   that changes when somebody publishes, i.e. rarely.

   The SHUFFLE still happens per visit: it lives in the component's useMemo,
   which reruns on mount regardless of where the data came from. So the feed
   keeps reshuffling exactly as before; only the fetching stops repeating. */

const FEED_POOL = 120;              // per collection, unchanged
const FEED_TTL_MS = 5 * 60 * 1000;  // same window the designer directory uses

let _feed = null;
let _feedAt = 0;
let _feedInflight = null;

export async function fetchFeedWork({ force = false } = {}) {
  const fresh = _feed && Date.now() - _feedAt < FEED_TTL_MS;
  if (fresh && !force) return _feed;
  // Two components mounting at once (dashboard + a re-render) must share one
  // request rather than each firing their own.
  if (_feedInflight && !force) return _feedInflight;

  const listOf = (name, kind) => getDocs(query(
    collection(db, name),
    where('visible', '==', true),
    orderBy('createdAt', 'desc'),
    limit(FEED_POOL),
  )).then(s => s.docs.map(d => ({ id: d.id, kind, ...d.data() })))
    .catch(() => []);

  _feedInflight = (async () => {
    try {
      // Photos and published 3D designs are separate collections but one feed —
      // the work is the work, and splitting it into two sections would make a
      // designer who publishes 3D look absent from the photo one.
      const [ph, de] = await Promise.all([
        listOf('photos', ITEM_KINDS.PHOTO),
        listOf('publishedDesigns', ITEM_KINDS.DESIGN),
      ]);
      _feed = [...ph, ...de];
      _feedAt = Date.now();
      return _feed;
    } finally {
      _feedInflight = null;
    }
  })();
  return _feedInflight;
}

/** Publishing or deleting work makes the pool wrong; drop it. */
export function invalidateFeedWork() {
  _feed = null;
  _feedAt = 0;
}

